import { GET_TECHNICIAN_ROLES_FOR_DROPDOWN } from '@nile/banyan/src/Graphql/Mutations/role';
import { GET_AI_AGENT_ENTITY_SHARE } from '@nile/banyan/src/Graphql/Query/ai';
import {
  EventEmitter,
  getParsedCrudData,
  getTranslatedText,
  Loader,
  useIntl,
  withErrorBoundary
} from '@nile/components';
import { AccountClient, useQuery } from '@nile/graphql/src';
import {
  Dropdown,
  RadioButton,
  Stack,
  TextStyle
} from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const RolePermission = ({
  featureId,
  updateParent = () => {},
  entityShare = null,
  hideHeading = false,
  noBorder = false,
  noTarget = false,
  menuPlacement,
  dropdownStyles,
  dropdownComponents
}) => {
  const intl = useIntl();
  const [baseRoleSelection, setBaseRoleSelection] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const [data, setData] = useState({
    roleShareType: 'ALL',
    addRoleIds: [],
    deleteRoleIds: []
  });

  const radioChange = useCallback(
    (_checked, newValue) => {
      const updatedData = {
        ...data,
        roleShareType: newValue
      };
      setData(updatedData);
      updateParent(updatedData);
    },
    [data, updateParent]
  );

  const [allRoleOptions, setAllRoleOptions] = useState([]);

  const { loading: AllClientsLoading, data: AllClientsData } = useQuery(
    GET_TECHNICIAN_ROLES_FOR_DROPDOWN,
    {
      client: AccountClient,
      fetchPolicy: 'network-only',
      variables: {
        listInfo: {
          sort: {
            attribute: 'name',
            order: 'ASC'
          }
        }
      },
      onCompleted: (res) => {
        const roles = res?.getTechnicianRoleTypeRoles || [];
        if (roles?.length > 0) {
          const roleOptions =
            roles?.map((role) => ({
              id: role.roleId,
              roleId: role.roleId,
              label: role.name,
              value: role.roleId
            })) || [];
          setAllRoleOptions(roleOptions);
        }
      }
    }
  );

  const onCompleted = (entityShareData) => {
    if (entityShareData) {
      const roleShareType = entityShareData?.roleShareType || 'ALL';
      const roles = entityShareData?.roles;

      if (roles?.length > 0) {
        const formattedRoles = roles?.map((role) => {
          return {
            id: role?.roleId,
            roleId: role?.roleId,
            label: role?.name,
            value: role?.roleId,
            key: role?.roleId,
            ...role
          };
        });
        setBaseRoleSelection(formattedRoles);
        setSelectedRoles(formattedRoles);
      }
      const updatedData = {
        id: entityShareData?.id,
        roleShareType,
        addRoleIds: [],
        deleteRoleIds: []
      };
      setData(updatedData);
      updateParent(updatedData);
    }
  };

  const skipAPI = !isEmpty(entityShare) && !entityShare?.id;
  const {
    loading: AiFeatureLoading,
    refetch: refetchEntityRoleShare
  } = useQuery(GET_AI_AGENT_ENTITY_SHARE, {
    fetchPolicy: 'network-only',
    client: AccountClient,
    skip: skipAPI,
    variables: {
      input: {
        featureId
      }
    },
    onCompleted: (res) => {
      const entityShareData = res?.getAIFeature?.entityShare;
      onCompleted(entityShareData);
    }
  });

  const updateSelection = (items) => {
    const { addedItems, deletedItems } = getParsedCrudData(
      baseRoleSelection,
      items,
      'roleId'
    );
    setSelectedRoles(items);
    const updatedData = {
      ...data,
      addRoleIds: addedItems,
      deleteRoleIds: deletedItems
    };
    setData(updatedData);
    updateParent(updatedData);
  };

  useEffect(() => {
    if (entityShare) {
      onCompleted(entityShare);
    }
  }, [entityShare]);

  useEffect(() => {
    EventEmitter.subscribe('refetchEntityRoleShare', refetchEntityRoleShare);
    return () => {
      EventEmitter.unsubscribe('refetchEntityRoleShare');
    };
  }, []);

  const roleShareType = data?.roleShareType;

  return (
    <>
      <div className="w100 full-height">
        {AiFeatureLoading ? (
          <div className="flex jctr">
            <Loader size="lg" />
          </div>
        ) : (
          <>
            {!hideHeading && (
              <div className="mb20">
                <div className="f-16 mb10">
                  <TextStyle weight="SemiBold">
                    <FormattedMessage id="admin.aiMarketplace.agent.rolePermission.access.heading" />
                  </TextStyle>
                </div>
                <div>
                  <TextStyle variation="subdued">
                    <FormattedMessage id="admin.aiMarketplace.agent.rolePermission.access.description" />
                  </TextStyle>
                </div>
              </div>
            )}

            <Stack vertical>
              <RadioButton
                label={getTranslatedText(
                  intl,
                  'admin.aiMarketplace.technicianVisibility.allRole'
                )}
                id="ALL"
                name="ALL"
                checked={roleShareType === 'ALL'}
                onChange={radioChange}
              />
              <RadioButton
                label={getTranslatedText(
                  intl,
                  'admin.aiMarketplace.technicianVisibility.specificRole'
                )}
                id="SPECIFIC"
                name="SPECIFIC"
                checked={roleShareType === 'SPECIFIC'}
                onChange={radioChange}
              />
            </Stack>
          </>
        )}
        <>
          {roleShareType === 'SPECIFIC' && (
            <div className="mt20">
              {AllClientsLoading ? (
                <div className="flex jctr">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <Dropdown
                    defaultValue={selectedRoles}
                    menuPortalTarget={noTarget ? null : document.body}
                    menuPlacement={menuPlacement || 'auto'}
                    value={selectedRoles}
                    isMulti
                    options={allRoleOptions}
                    onChange={updateSelection}
                    isClearable={false}
                    noBorder={!!noBorder}
                    styles={dropdownStyles ? dropdownStyles() : undefined}
                    components={dropdownComponents || null}
                  />
                </>
              )}
            </div>
          )}
        </>
      </div>
    </>
  );
};

export default withErrorBoundary(RolePermission);
